import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { uploadImageWithPresignedUrl } from '../../api/tournament/fixturesService';
import { getImageUrl } from '../../utils/imageUrl';
import './SeasonHeroImageUpload.css';

export interface SeasonHeroImageUploadProps {
  value: string | null;
  label: string;
  helperText?: string;
  context: string;
  previewAspectRatio?: string;
  fallbackValue?: string | null;
  fallbackLabel?: string;
  disabled?: boolean;
  onChange: (value: string | null) => void;
  onUploadingChange: (uploading: boolean) => void;
}

const SeasonHeroImageUpload: React.FC<SeasonHeroImageUploadProps> = ({
  value,
  label,
  helperText,
  context,
  previewAspectRatio = '16 / 9',
  fallbackValue = null,
  fallbackLabel,
  disabled = false,
  onChange,
  onUploadingChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(value);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewKey(value);
  }, [value]);

  useEffect(() => () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
  }, [localPreviewUrl]);

  const updateUploading = (uploading: boolean) => {
    setIsUploading(uploading);
    onUploadingChange(uploading);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    setError(null);
    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(objectUrl);
    updateUploading(true);

    try {
      const key = await uploadImageWithPresignedUrl(file, context);
      setPreviewKey(key);
      setLocalPreviewUrl(null);
      onChange(key);
    } catch {
      setLocalPreviewUrl(null);
      setError(`${label} upload failed. Please try again.`);
    } finally {
      updateUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    setError(null);
    setPreviewKey(null);
    setLocalPreviewUrl(null);
    onChange(null);
  };

  const hasOwnImage = Boolean(previewKey);
  const fallbackPreviewUrl = !hasOwnImage && fallbackValue ? getImageUrl(fallbackValue) : null;
  const previewUrl = localPreviewUrl || getImageUrl(previewKey) || fallbackPreviewUrl;

  return (
    <Form.Group className="mb-4">
      <Form.Label>{label}</Form.Label>
      <div className="season-hero-upload">
        <button
          type="button"
          className="season-hero-upload__preview"
          style={{ aspectRatio: previewAspectRatio }}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          aria-label={previewUrl ? `Change ${label}` : `Upload ${label}`}
        >
          {previewUrl ? (
            <img src={previewUrl} alt={`${label} preview`} />
          ) : (
            <span className="season-hero-upload__placeholder">
              <i className="ri-image-add-line" />
              <span>Upload {label}</span>
            </span>
          )}
          {!hasOwnImage && fallbackPreviewUrl && fallbackLabel && !isUploading && (
            <span className="season-hero-upload__fallback">{fallbackLabel}</span>
          )}
          {isUploading && (
            <span className="season-hero-upload__overlay" role="status">
              <span className="spinner-border spinner-border-sm" />
              Uploading...
            </span>
          )}
        </button>
        {hasOwnImage && previewUrl && !isUploading && (
          <>
            <button type="button" className="season-hero-upload__replace" onClick={() => fileInputRef.current?.click()} disabled={disabled} aria-label={`Replace ${label}`}>
              <i className="ri-pencil-line" />
            </button>
            <button type="button" className="season-hero-upload__remove" onClick={handleRemove} disabled={disabled} aria-label={`Remove ${label}`}>
              <i className="ri-delete-bin-line" />
            </button>
          </>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" aria-label={label} disabled={disabled || isUploading} onChange={handleFileChange} hidden />
      </div>
      {helperText && <Form.Text className="text-muted">{helperText}</Form.Text>}
      {error && <div className="document-upload-error" role="alert"><i className="ri-error-warning-line" />{error}</div>}
    </Form.Group>
  );
};

export default SeasonHeroImageUpload;
